import { isEmptyString, isRecord, value } from '@takeshape/util';
import { JSONPath } from 'jsonpath-plus';
import unset from 'lodash/unset.js';
import { CURRENT_OPEN_API_TRANSFORM_VERSION } from "../constants.js";
import { hasEveryKey } from "../util/has.js";
import { getHealthCheck } from "../util/health.js";
import { fixObfuscatedProp } from "../util/props.js";
/**
 * Create a new copy and collapse unnecessary combos (anyOf, allOf, oneOf)
 * Ex: {"oneOf": [{"$ref": "#/foo/bar"}]} -> {"$ref": "#/foo/bar"}
 */
export function collapseUnnecessaryCombos(doc) {
    const normalize = (obj) => {
        if (Array.isArray(obj)) {
            return obj.map(normalize);
        }
        if (isRecord(obj)) {
            const result = {};
            for (const key of Object.keys(obj)) {
                const value = obj[key];
                if ((key === 'anyOf' || key === 'oneOf' || key === 'allOf') && Array.isArray(value) && value.length === 1) {
                    Object.assign(result, normalize(value[0]));
                }
                else {
                    result[key] = normalize(value);
                }
            }
            return result;
        }
        return obj;
    };
    return normalize(doc);
}
const serviceProvider = {
    name: 'stripe',
    title: 'Stripe',
    description: 'Stripe is a technology company that builds economic infrastructure for the internet. Businesses of every size—from new startups to public companies—use our software to accept payments and manage their businesses online.',
    categories: ['ecommerce'],
    links: [
        { title: 'Homepage', url: 'https://stripe.com/' },
        { title: 'Docs', url: '/docs/services/providers/stripe' }
    ],
    serviceType: 'openapi',
    introspectionType: 'openapi',
    createId: value('stripe'),
    getFormSchema: value({
        type: 'object',
        required: ['title', 'id', 'namespace', 'authentication'],
        properties: {
            title: {
                title: 'Name',
                type: 'string',
                default: 'Stripe'
            },
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '[0-9a-z_-]+',
                default: 'stripe'
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: 'Stripe'
            },
            webhookSecret: {
                title: 'Webhook Secret',
                type: 'string'
            },
            authentication: {
                title: 'Authentication',
                description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
                type: 'object',
                properties: {
                    token: {
                        title: 'API Key',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['token']
            }
        }
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'webhookSecret', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable name for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'A unique identifier for this service. Only lowercase letters, numbers, hyphen, and underscore are allowed.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            webhookSecret: {
                widget: 'singleLineText',
                instructions: 'A webhook secret, used to verify Stripe webhooks.'
            },
            authentication: {
                properties: {
                    token: {
                        widget: 'sensitiveField',
                        instructions: 'Secret API Key from Stripe > Developers > API keys'
                    }
                },
                order: ['token']
            }
        }
    }),
    getConfig(formData) {
        const config = {
            id: formData.id,
            title: formData.title,
            provider: 'stripe',
            serviceType: 'openapi',
            authenticationType: 'bearer',
            namespace: formData.namespace,
            options: {
                endpoint: 'https://api.stripe.com/',
                spec: 'https://raw.githubusercontent.com/stripe/openapi/master/openapi/spec3.json',
                transformVersion: CURRENT_OPEN_API_TRANSFORM_VERSION,
                useRestfulNames: true,
                webhookSecret: formData.webhookSecret
            }
        };
        if (!formData.authentication) {
            return config;
        }
        if (isEmptyString(formData.authentication.token)) {
            config.authentication = null;
            return config;
        }
        config.authentication = {
            type: 'bearer',
            token: fixObfuscatedProp(formData.authentication.token)
        };
        return config;
    },
    isCompleteConfig: hasEveryKey(['authentication', 'options.endpoint', 'options.spec']),
    specTransform(spec) {
        const cloned = collapseUnnecessaryCombos(spec);
        const callback = (payload) => {
            const pathArray = JSONPath.toPathArray(payload).slice(1);
            const lastElement = pathArray.slice(-1);
            if (lastElement[0] === 'requestBody') {
                if (pathArray.slice(-2)[0] === 'get') {
                    unset(cloned, pathArray);
                }
            }
        };
        JSONPath({
            path: '$.paths.[requestBody,parameters]',
            resultType: 'path',
            json: spec,
            callback
        });
        return cloned;
    },
    getHealthCheck: getHealthCheck({
        checkName: 'ping',
        // 200 or 401 on bad auth
        endpoint: 'https://api.stripe.com/v1/customers'
    })
};
export default serviceProvider;
